import { createPortal } from "react-dom";
import { useState } from "react";
import { XIcon, Loader2, UserIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { cn } from "@/lib/utils";
import { styles } from "../styles";
import { usePatchRosterMemberMutation } from "../../api/endpoints/roster";
import { useGetCurrentLocalFacilityId } from "@/modules/facility/facilityHooks";
import type { Clubhouse_LeagueV2RosterMemberDto } from "../../types/leagueRoster";
import type { Clubhouse_PatchLeagueV2RosterMember } from "../../types/leagueRoster";
import { TEE_TYPE_OPTIONS, teeLabel, teeSetIdOf, toRosterTeeType } from "../helpers";
import { RosterTeeType } from "../../enums";
import { RoleSelector } from "./RoleSelector";
import { ApiError, fieldLabelCls } from "./fieldPrimitives";

interface EditRosterMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leagueId: string;
  member: Clubhouse_LeagueV2RosterMemberDto;
  onUpdated?: () => void;
  /** League's home facility; falls back to the current facility when omitted. */
  facilityId?: string;
  /**
   * When set, the Active switch cannot turn a full-time player off. Started
   * leagues withdraw through the roster action, or refuse the change.
   */
  activeLockedMessage?: string;
}

// Declared rather than inferred: in an object literal the tee's "" widens to
// `string`, which would let a raw select value reach the typed patch body.
type EditFields = {
  teeType: RosterTeeType | "";
  isActive: boolean;
  isSubstituteOnly: boolean;
};

export function EditRosterMemberDialog({
  open,
  onOpenChange,
  leagueId,
  member,
  onUpdated,
  facilityId: facilityIdProp,
  activeLockedMessage,
}: EditRosterMemberDialogProps) {
  const currentFacilityId = useGetCurrentLocalFacilityId();
  const facilityId = facilityIdProp ?? currentFacilityId;
  const [patchRosterMember, { isLoading }] = usePatchRosterMemberMutation();
  const [apiError, setApiError] = useState<string | null>(null);
  const [fields, setFields] = useState<EditFields>({
    // "" is the unassigned choice, not "unchanged" — it saves as an explicit
    // null. Normalized on the way in as well as at the query: the member
    // arrives as a prop, so nothing here guarantees it came from a normalized
    // read, and a numeric value seeded raw would save as a wipe.
    teeType: toRosterTeeType(member.teeType) ?? "",
    isActive: member.isActive ?? true,
    isSubstituteOnly: member.isSubstituteOnly ?? false,
  });

  async function handleSave() {
    if (!facilityId || !member.memberId) return;
    setApiError(null);
    const body: Clubhouse_PatchLeagueV2RosterMember = {
      // Handicap is established when the player is added. Seed, current, and
      // status are left off the body so a save cannot overwrite them.
      //
      // The tee is always sent, because the select always states an intent: a
      // tee, or null for unassigned. Sending undefined here would omit the key
      // and the server would read a deliberate clear as "leave it alone".
      //
      // Converted to the numeric id the write side takes — the select works in
      // the name domain the roster GET returns.
      // "" means the member had no tee and still has none: teeSetIdOf maps it
      // to undefined, which drops the key and leaves the stored value alone.
      // A clear would need an explicit null, which PATCH currently discards.
      ...(member.teeLocked
        ? {}
        : { teeSetId: teeSetIdOf(fields.teeType || undefined) }),
      isActive: fields.isActive,
      isSubstituteOnly: fields.isSubstituteOnly,
    };
    try {
      await patchRosterMember({
        facilityId,
        leagueId,
        memberId: member.memberId,
        body,
      }).unwrap();
      onUpdated?.();
      onOpenChange(false);
    } catch {
      setApiError("Failed to update player. Please try again.");
    }
  }

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[200] grid place-items-center">
      <button
        type="button"
        aria-label="Dismiss dialog"
        className="absolute inset-0 bg-black/30 backdrop-blur-sm motion-safe:animate-in motion-safe:fade-in-0 motion-safe:duration-200"
        onClick={() => onOpenChange(false)}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Edit Player"
        className="relative z-10 flex flex-col w-full max-w-lg max-h-[90vh] rounded-2xl bg-popover text-sm text-popover-foreground shadow-xl motion-safe:animate-in motion-safe:fade-in-0 motion-safe:zoom-in-95 motion-safe:slide-in-from-bottom-2 motion-safe:duration-200"
      >
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="absolute top-2 right-2 inline-flex items-center justify-center size-9 rounded-full bg-primary/6 text-primary hover:bg-primary/12 transition-colors"
        >
          <XIcon className="size-4" />
          <span className="sr-only">Close</span>
        </button>

        <div className="flex-1 overflow-y-auto px-6 pt-6 pb-6">
          <h2 className="poppins mb-6 text-xl font-bold leading-none tracking-tight">
            Edit Player
          </h2>

          <div className="flex flex-col gap-4">
            {/* Identity header, same construction as the add dialog's: the
                dialog's own entrance is the authored moment, so the card and
                its avatar no longer stage two more delayed animations on top
                of it. The accent fill went too, per the restraint rule — this
                is workflow chrome, not a competitive surface. */}
            <div className="flex items-center gap-3 rounded-xl border border-border bg-surface/50 px-4 py-3">
              <div className="size-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <UserIcon className="size-4 text-primary" />
              </div>
              <p className="poppins text-sm font-medium text-text-primary">
                {member.playerName ?? "Player"}
              </p>
            </div>

            <FieldGroup className="gap-4">
              <div className="flex flex-col gap-1">
                <p className={fieldLabelCls}>aG Handicap</p>
                <p className="text-lg font-medium tabular-nums text-text-primary monoNumber">
                  {member.currentHandicap != null
                    ? member.currentHandicap.toFixed(1)
                    : "—"}
                </p>
              </div>

              <Field className="gap-2">
                <FieldLabel htmlFor="edit-tee" className={fieldLabelCls}>
                  Tee
                </FieldLabel>
                <select
                  id="edit-tee"
                  className={styles.wizardInputCls}
                  value={fields.teeType}
                  disabled={member.teeLocked}
                  onChange={(e) =>
                    setFields({
                      ...fields,
                      teeType: e.target.value as RosterTeeType | "",
                    })
                  }
                >
                  {/* Disabled, not absent. A member with no tee needs a value
                      the select can display, and dropping the option would
                      silently land them on the first real tee. Disabled also
                      means an assigned tee cannot be cleared here, which is the
                      workaround for PATCH discarding an explicit null — but an
                      already-unassigned member can still save untouched, so
                      editing Role or Active never forces a tee. */}
                  <option value="" disabled>
                    Select a tee…
                  </option>
                  {TEE_TYPE_OPTIONS.map((tee) => (
                    <option key={tee} value={tee}>
                      {teeLabel(tee)}
                    </option>
                  ))}
                </select>
                <FieldDescription>
                  {member.teeLocked
                    ? "This tee was set when the player was added. It locks once Round 1 has started."
                    : "Starts at the tee this player was given when they were added. You can change it until Round 1 starts."}
                </FieldDescription>
              </Field>

              <RoleSelector
                substituteOnly={fields.isSubstituteOnly}
                onChange={(v) => setFields({ ...fields, isSubstituteOnly: v })}
              />

              <Field orientation="horizontal">
                <FieldLabel
                  htmlFor="edit-isActive"
                  className={cn(fieldLabelCls, "cursor-pointer select-none")}
                >
                  Active
                </FieldLabel>
                <Switch
                  id="edit-isActive"
                  checked={fields.isActive}
                  disabled={!!activeLockedMessage}
                  onCheckedChange={(checked) =>
                    setFields({ ...fields, isActive: checked })
                  }
                />
              </Field>
              {activeLockedMessage && (
                <FieldDescription>{activeLockedMessage}</FieldDescription>
              )}
            </FieldGroup>

            {apiError && <ApiError message={apiError} />}
          </div>
        </div>

        <div className="shrink-0 border-t border-border px-6 py-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="font-bold text-primary rounded-md"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void handleSave()}
            disabled={isLoading}
            className="bg-primary font-bold text-white rounded-md"
          >
            {isLoading ? (
              <>
                <Loader2 className="animate-spin" />
                Saving…
              </>
            ) : (
              "Save"
            )}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
